import type { SunburstChartProps } from "../components/SunBurstChart";
import type { EvolutionChartProps } from "../components/EvolutionChart";
import type { TextCardContentProps } from "../components/TextCardContent";
import type { WeatherInfoCardContentProps } from "../components/WeatherInfoCardContent";
import type { AccumulatedCertifiedEnergyProps } from "../components/metas/AccumulatedCertifiedEnergyChart";
import type { AccumulatedEnergyProps } from "../components/metas/AccumulatedEnergyChart";
import type { LossDiagramProps } from "../components/metas/LossDiagramChart";
import type { ProductivityProps } from "../components/metas/ProductivityChart";
import {
  BaseErrorCard,
  EnumRopCards,
  type RopCard,
} from "../models/dados-indicadores.model";
import { CardTypesEnum } from "../types";
import {
  formatNumber,
  formatNumberCurrencyBRL,
  formatNumberPercent,
} from "./format-number";
import { ParetoChartProps } from "../components/ParetoChart";
import { TrackerChartProps } from "../components/TrackerChart";
import { HeatmapChartProps } from "../components/HeatmapChart";

export type Card01PrRealizadoRopCard = {
  type: CardTypesEnum.PRRealizado;
  data: TextCardContentProps;
};

export type Card02PerformanceRatioRopCard = {
  type: CardTypesEnum.PerformanceRatio;
  data: TextCardContentProps;
};

export type Card03EnergiaGeradaRopCard = {
  type: CardTypesEnum.EnergiaGerada;
  data: TextCardContentProps;
};

export type Card04ValorGeradoRopCard = {
  type: CardTypesEnum.ValorGerado;
  data: TextCardContentProps;
};

export type Card05DisponibilidadeRopCard = {
  type: CardTypesEnum.Disponibilidade;
  data: TextCardContentProps;
};

export type Card06EstacaoSolarimetricaInfoRopCard = {
  type: CardTypesEnum.Ambiente;
  data: WeatherInfoCardContentProps;
};

export type Card07GraficoEvolucaoChartRopCard = {
  type: CardTypesEnum.EvolucaoGeracao;
  data: EvolutionChartProps;
};

export type Card08EnergiaGeradaChartRopCard = {
  type: CardTypesEnum.GeracaoPlanta;
  data: SunburstChartProps;
};

export type Card09EnergiaAcumuladaRopCard = {
  type: CardTypesEnum.EnergiaAcumulada;
  data: AccumulatedEnergyProps;
};

export type Card10DiagramaDePerdasRopCard = {
  type: CardTypesEnum.DiagramaPerdas;
  data: LossDiagramProps;
};

export type Card11EnergiaAcumuladaXEnergiaCertificadaRopCard = {
  type: CardTypesEnum.EnergiaAcumEnergiaCert;
  data: AccumulatedCertifiedEnergyProps;
};

export type Card12ProdutividadeRopCard = {
  type: CardTypesEnum.Produtividade;
  data: ProductivityProps;
};

export type Card15ParetoRopCard = {
  type: CardTypesEnum.Pareto;
  data: ParetoChartProps;
};

export type Card17TensaoDeLinhaRopCard = {
  type: CardTypesEnum.TensaoDeLinha;
  data: TextCardContentProps;
};

export type Card18TrackerRopCard = {
  type: CardTypesEnum.Tracker;
  data: TrackerChartProps;
};

export type Card19HeatmapRopCard = {
  type: CardTypesEnum.Heatmap;
  data: HeatmapChartProps;
};

export type Card20PotenciaAtivaRopCard = {
  type: CardTypesEnum.PotenciaAtiva;
  data: TextCardContentProps;
};

export type Card21EnergiaAcumuladaRopCard = {
  type: CardTypesEnum.EnergiaAcumuladaMWh;
  data: TextCardContentProps;
};

export type CardTypeAndData = {
  ropCard: RopCard;
  card:
    | Card01PrRealizadoRopCard
    | Card02PerformanceRatioRopCard
    | Card03EnergiaGeradaRopCard
    | Card04ValorGeradoRopCard
    | Card05DisponibilidadeRopCard
    | Card06EstacaoSolarimetricaInfoRopCard
    | Card07GraficoEvolucaoChartRopCard
    | Card08EnergiaGeradaChartRopCard
    | Card09EnergiaAcumuladaRopCard
    | Card10DiagramaDePerdasRopCard
    | Card11EnergiaAcumuladaXEnergiaCertificadaRopCard
    | Card12ProdutividadeRopCard
    | Card15ParetoRopCard
    | Card17TensaoDeLinhaRopCard
    | Card18TrackerRopCard
    | Card19HeatmapRopCard
    | Card20PotenciaAtivaRopCard
    | Card21EnergiaAcumuladaRopCard;
};

export function ropCardEventToCard(ropCard: RopCard): CardTypeAndData {
  switch (ropCard.type) {
    case EnumRopCards.RopCard01PrRealizado:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.PRRealizado,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            value: formatNumberPercent(ropCard.value, 2),
            cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
            cardType: ropCard.type,
          },
        },
      };
    case EnumRopCards.RopCard02PerformanceRatio:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.PerformanceRatio,

          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            value: formatNumber(ropCard.value, 2),
            secondaryData: [
              {
                value: `${formatNumberPercent(
                  ropCard.prPrevisto,
                  2
                )} do previsto`,
              },
            ],
            backgroundStyle: ropCard.prPrevisto > 0 ? "positive" : "negative",
            cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
            cardType: ropCard.type,
          },
        },
      };
    case EnumRopCards.RopCard03EnergiaGerada:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.EnergiaGerada,

          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            planta: ropCard.planta,
            value: formatNumber(
              ropCard.planta.values.gerada.value ?? 0,
              2,
              "MWh"
            ),
            secondaryData: [
              {
                value: `${formatNumberPercent(
                  ropCard.planta.values.previstaPercentual.value ?? 0,
                  2
                )} do previsto`,
              },
            ],
            backgroundStyle: (ropCard.planta.values.previstaPercentual.value ?? 0) > 0 ? "positive" : "negative",
            cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
            cardType: ropCard.type,
          },
        },
      };
    case EnumRopCards.RopCard04ValorGerado:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.ValorGerado,

          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            planta: ropCard.planta,
            value: formatNumberCurrencyBRL(
              ropCard.planta.values.gerado.value ?? 0
            ),
            backgroundStyle: (ropCard.planta.values.previstoPercentual.value ?? 0) > 0 ? "positive" : "negative",
            cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
            cardType: ropCard.type,
          },
        },
      };
    case EnumRopCards.RopCard05Disponibilidade:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.Disponibilidade,

          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            planta: ropCard.planta,
            value: formatNumberPercent(
              ropCard.planta.values.value.value
                ? ropCard.planta.values.value.value / 100
                : 0,
              2
            ),
            cardType: ropCard.type,
            hourlyValues: ropCard.planta.values.hourlyValues
              ? ropCard.planta.values.hourlyValues.map((item) => {
                  return {
                    ...item,
                    value: formatNumberPercent(Number(item.value) ?? 0, 2),
                  };
                })
              : [],
            cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
          },
        },
      };
    case EnumRopCards.RopCard06EstacaoSolarimetrica:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.Ambiente,
          data: {
            weather: {
              ambientTemperature:
                ropCard.estacaoSolimetrica.temperaturaAmbiente,
              moduleTemperature: ropCard.estacaoSolimetrica.temperaturaModulo,
              relativeHumidity: ropCard.estacaoSolimetrica.umidadeRelativa,
              windSpeed: ropCard.estacaoSolimetrica.velocidadeVento,
              irradiation: ropCard.estacaoSolimetrica.irradiacaoInclinada,
            },
            dataHoraCaptura: ropCard.dataHoraCaptura,
            cardError: parseErrorAsJson(ropCard.errorAsJson),
            lastUpdate: new Date(),
          },
        },
      };
    case EnumRopCards.RopCard07GraficoEvolucaoChart:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.EvolucaoGeracao,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            cardType: ropCard.type,
            data: {
              evolution: ropCard.planta.values.evolution,
              irradiancia: ropCard.planta.values.irradiancia,
              potenciaAtivaAtMinutes:
                ropCard.planta.values.potenciaAtivaAtMinutes,
              irradianciaMediaAtMinutes:
                ropCard.planta.values.irradianciaMediaAtMinutes,
              planta: ropCard.planta,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard13EnergiaPorEquipamento:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.GeracaoPlanta,
          data: {
            cardType: ropCard.type,
            title: ropCard.title,
            lastUpdate: new Date(),
            data: {
              planta: ropCard.planta,
              plantaMinutes: ropCard.plantaMinutes,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard09EnergiaAcumulada:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.EnergiaAcumulada,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            cardType: ropCard.type,
            data: {
              accumulatedIdealEnergy: ropCard.energiaIdealAcumulada,
              accumulatedGeneratedEnergy: ropCard.energiaGeradaAcumulada,
              pAccumulated: ropCard.p50Acumulada,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard10DiagramaDePerdas:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.DiagramaPerdas,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            cardType: ropCard.type,
            data: {
              perdaNoCircuito: ropCard.perdaNoCircuito,
              energiaIdeal: ropCard.energiaIdeal,
              perdaDoTrafo: ropCard.perdaDoTrafo,
              perdaDosInversores: ropCard.perdaDosInversores,
              perdaPorIndisponibilidade: ropCard.perdaPorIndisponibilidade,
              outrasPerdas: ropCard.outrasPerdas,
              energiaGerada: ropCard.energiaGerada,
              lastCollectTime: ropCard.lastCollectTime,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard11EnergiaAcumuladaXEnergiaCertificada:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.EnergiaAcumEnergiaCert,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            cardType: ropCard.type,
            data: {
              generatedEnergy: ropCard.generatedEnergy,
              pFifty: ropCard.energiaCertificadaP50Hourly,
              pSeventyFive: ropCard.energiaCertificadaP75Hourly,
              pNinety: ropCard.energiaCertificadaP90Hourly,
              pNinetyFive: ropCard.energiaCertificadaP95Hourly,
              energiaCertificadaP50NormalizedValues:
                ropCard.energiaCertificadaP50NormalizedValues,
              energiaCertificadaP75NormalizedValues:
                ropCard.energiaCertificadaP75NormalizedValues,
              energiaCertificadaP90NormalizedValues:
                ropCard.energiaCertificadaP90NormalizedValues,
              energiaCertificadaP95NormalizedValues:
                ropCard.energiaCertificadaP95NormalizedValues,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard12Produtividade:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.Produtividade,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            cardType: ropCard.type,
            data: {
              generatedEnergy: ropCard.generatedEnergy,
              predictedEnergy: ropCard.predictedEnergy,
              irradiance: ropCard.irradiance,
              predictedIrradianceMonths: ropCard.predictedIrradianceMonths,
              expectedIrradianciaAtDay: ropCard.expectedIrradianciaAtDay,
              expectedIrradianciaAtHour: ropCard.expectedIrradianciaAtHour,
              expectedEnergyAtDay: ropCard.expectedEnergyAtDay,
              expectedEnergyAtHour: ropCard.expectedEnergyAtHour,
              expectedIrradianciaNormalizedAtHour:
                ropCard.expectedIrradianciaNormalizedAtHour,
              expectedEnergyNormalizedAtHour:
                ropCard.expectedEnergyNormalizedAtHour,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard15Pareto:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.Pareto,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            cardType: ropCard.type,
            data: {
              alarms: ropCard.alarms,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard17TensaoDeLinha:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.TensaoDeLinha,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            value: formatNumber(ropCard.tensaoDeLinhakV, 2, "kV"),
            cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
            cardType: ropCard.type,
          },
        },
      };
    case EnumRopCards.RopCard18Tracker:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.Tracker,
          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            data: {
              expectedAngle: ropCard.expectedAngle,
              trackers: ropCard.trackers,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard19Heatmap:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.Heatmap,
          data: {
            title: ropCard.title,
            cardType: ropCard.type,
            lastUpdate: new Date(),
            data: {
              inversores: ropCard.inversores,
              cardError: parseErrorAsJson(ropCard.errorAsJson),
            },
          },
        },
      };
    case EnumRopCards.RopCard20PotenciaAtiva:
      return {
        ropCard,
        card: {
          type: CardTypesEnum.PotenciaAtiva,

          data: {
            title: ropCard.title,
            lastUpdate: new Date(),
            value: formatNumber(ropCard.potenciaAtiva?.value ?? 0, 2, "MW"),
            cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
            cardType: ropCard.type,
          },
        },
      };
      case EnumRopCards.RopCard21EnergiaAcumulada:
        return {
          ropCard,
          card: {
            type: CardTypesEnum.EnergiaAcumuladaMWh,
            data: {
              title: ropCard.title,
              lastUpdate: new Date(),
              value: formatNumber(ropCard.energiaAcumulada?.value ?? 0, 2, "MWh"),
              cardError: parseErrorAsJson(ropCard.errorAsJson ?? ""),
              cardType: ropCard.type,
            },
          },
        };
    default:
      console.error(`Tipo de card não mapeado: ${ropCard}`, ropCard);
      throw new Error(`Tipo de card não mapeado: ${ropCard}`);
  }
}

function parseErrorAsJson(errorAsJson: string): BaseErrorCard {
  const parseError = JSON.parse(errorAsJson);
  return parseError;
}

export function getRopCardName(cardType: number) {
  switch (cardType) {
    case 1:
      return "PR Realizado";
    case 2:
      return "Performance Ratio";
    case 3:
      return "Energia Gerada";
    case 4:
      return "Valor Gerado";
    case 5:
      return "Disponibilidade";
    case 6:
      return "Estação Solarimétrica";
    case 7:
      return "Gráfico de Evolução";
    case 8:
      return "Energia por Equipamento";
    case 9:
      return "Energia Acumulada";
    case 10:
      return "Diagrama de Perdas";
    case 11:
      return "Energia Acumulada x Energia Certificada";
    case 12:
      return "Produtividade";
    case 13:
      return "Energia por Equipamento";
    case 14:
      return "Portfolio SSE";
    case 15:
      return "Pareto de Alarmes";
    case 16:
      return "Alarmes";
    case 17:
      return "Tensão de Linha";
    case 18:
      return "Tracker fora de posição";
    case 19:
      return "Potência Ativa dos Inversores";
    case 20:
      return "Potência Ativa";
    case 21:
      return "Energia Acumulada Mensal MWh";
    default:
      return "";
  }
}
